import * as Sentry from '@sentry/nextjs'

export type BrowserInfo = {
  name: string
  os: string
  platform: 'ios' | 'android' | 'desktop' | 'unknown'
  isInApp: boolean
}

const IN_APP_BROWSERS_REGEX = new RegExp(
  [
    'FBAN',
    'FBAV', // Facebook
    'Instagram', // Instagram
    'Line', // Line
    'KAKAOTALK', // Kakaotalk
    'Twitter', // Twitter
    'LinkedIn', // LinkedIn
    'TikTok', // TikTok
    'Snapchat', // Snapchat
    'Pinterest', // Pinterest
    'Hike', // Hike
    'Viber', // Viber
    'Wirtschaftswoche', // Wirtschaftswoche
    'Thunderbird', // Thunderbird
    'Telegram', // Telegram
    'MicroMessenger', // WeChat
    'WeChat', // WeChat
  ].join('|'),
  'i',
)

/**
 * Detects platform category
 */
export const detectPlatform = (osName: string, ua: string): BrowserInfo['platform'] => {
  const name = osName.toLowerCase()
  if (name.includes('ios') || name.includes('mac') || /iPad|iPhone|iPod/.test(ua)) {
    return 'ios'
  }
  if (name.includes('android')) {
    return 'android'
  }
  if (name.includes('windows') || name.includes('linux')) {
    return 'desktop'
  }
  return 'unknown'
}

const WV_REGEX = /wv\)/
const WEBVIEW_REGEX = /WebView/
const SAFARI_REGEX = /Safari/
const ANDROID_REGEX = /Android/
const CHROME_REGEX = /Chrome/
const VERSION_4_REGEX = /Version\/4\.0/

/**
 * Core detection logic for In-App Browsers
 */
export const detectInApp = (ua: string, browserName: string): boolean => {
  // 1. Check against known In-App Browser list
  const isKnownInApp = IN_APP_BROWSERS_REGEX.test(ua)

  if (isKnownInApp) return true

  // 2. Check for generic WebView indicators
  const isWebView =
    WV_REGEX.test(ua) || // Common Android WebView pattern
    WEBVIEW_REGEX.test(ua) || // Explicit WebView UA
    (browserName === 'WebKit' && !SAFARI_REGEX.test(ua)) // WebKit without Safari typically means WebView on iOS

  if (isWebView) return true

  // 3. Android-specific check (Lollipop and above)
  // "Version/4.0" in Chrome UA string usually indicates WebView
  if (ANDROID_REGEX.test(ua) && VERSION_4_REGEX.test(ua) && CHROME_REGEX.test(ua)) {
    return true
  }

  return false
}

const iosSafariHttps = (url: string) => {
  const safariUrl = url.replace(/^https?:\/\//, 'x-safari-https://')
  window.location.href = safariUrl
  return true
}

const androidIntent = (url: string) => {
  const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${encodeURIComponent(
    url,
  )};end`

  window.location.href = intentUrl
  return true
}

const windowOpenBlank = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow && !newWindow.closed) {
    newWindow.focus()
    return true
  }
  return false
}

const windowOpenTop = (url: string) => {
  const newWindow = window.open(url, '_top')
  return !!newWindow
}

const anchorClick = (url: string) => {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)

  const clickEvent = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
  })

  anchor.dispatchEvent(clickEvent)
  setTimeout(() => document.body.removeChild(anchor), 100)
  return true
}

const formSubmit = (url: string) => {
  const form = document.createElement('form')
  form.method = 'GET'
  form.action = url
  form.target = '_blank'
  form.style.display = 'none'
  document.body.appendChild(form)
  form.submit()

  setTimeout(() => document.body.removeChild(form), 100)
  return true
}

const locationAssignDelayed = (url: string) => {
  const timeout = setTimeout(() => {
    window.location.assign(url)
  }, 100)

  const newWindow = window.open('', '_blank')
  if (newWindow) {
    clearTimeout(timeout)
    newWindow.location.href = url
    newWindow.focus()
    return true
  }
  return false
}

const locationHref = (url: string) => {
  window.location.href = url
  return true
}

/**
 * Executes strategies sequentially with small delays
 */
const executeStrategies = async (
  strategies: Array<(url: string) => boolean>,
  url: string,
): Promise<void> => {
  for (let i = 0; i < strategies.length; i++) {
    const strategy = strategies[i]
    console.log(`[IAB Escape] Trying strategy ${i + 1}/${strategies.length}: ${strategy.name}`)

    try {
      if (strategy(url)) {
        console.log('[IAB Escape] Redirect successful with strategy:', strategy.name)
        Sentry.logger.info(`IAB Breakout Success: ${strategy.name}`, {
          extra: { url, strategy: strategy.name, ua: navigator.userAgent },
        })
        return
      }
    } catch (error) {
      Sentry.logger.error(`[IAB Escape] Strategy ${strategy.name} failed:`, { extra: { error } })
    }

    if (i < strategies.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
  }

  console.warn('[IAB Escape] All strategies failed, using final fallback')
  window.location.href = url

  Sentry.logger.error('IAB Breakout Failed: generic fallback used', {
    extra: { url, ua: navigator.userAgent },
  })
}

/**
 * Gets redirect strategies based on platform and app
 */
const getRedirectStrategies = (browserInfo: BrowserInfo): Array<(url: string) => boolean> => {
  const strategies: Array<(url: string) => boolean> = []

  // Platform-specific optimizations
  if (browserInfo.platform === 'ios') {
    strategies.push(iosSafariHttps)
  } else if (browserInfo.platform === 'android') {
    strategies.push(androidIntent)
  }

  // Universal strategies (fallback for all platforms)
  strategies.push(
    windowOpenBlank,
    windowOpenTop,
    anchorClick,
    formSubmit,
    locationAssignDelayed,
    locationHref,
  )

  return strategies
}

/**
 * Performs the redirect to open in external browser
 */
const performRedirect = async (url: string, browserInfo: BrowserInfo): Promise<void> => {
  console.log('[IAB Escape] Starting enhanced redirect for:', url)
  console.log('[IAB Escape] Browser info:', browserInfo)

  Sentry.logger.info('IAB Escape Initiated', {
    level: 'info',
    extra: {
      url,
      browser: browserInfo.name,
      platform: browserInfo.platform,
      ua: navigator.userAgent,
    },
  })

  // Enhanced redirect strategies with multiple fallbacks
  const strategies = getRedirectStrategies(browserInfo)
  console.log(`[IAB Escape] Found ${strategies.length} strategies to try`)

  await executeStrategies(strategies, url)
}

export const stripIabEscapeParam = (url: string) => {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('iab_escape')
    return urlObj.toString()
  } catch {
    return url.replace(/[?&]iab_escape=true/, '').replace(/\?$/, '')
  }
}

export const escapeInAppBrowser = (
  url: string | undefined,
  isAutoEscape: boolean,
  browserInfo: BrowserInfo,
): void => {
  let targetUrl = url || window.location.href

  if (isAutoEscape) {
    // Append a flag to prevent infinite loops when auto-escaping
    try {
      const urlObj = new URL(targetUrl)
      if (!urlObj.searchParams.has('iab_escape')) {
        urlObj.searchParams.set('iab_escape', 'true')
        targetUrl = urlObj.toString()
      }
    } catch {
      // Fallback if URL parsing fails
      if (!targetUrl.includes('iab_escape=')) {
        targetUrl += (targetUrl.includes('?') ? '&' : '?') + 'iab_escape=true'
      }
    }
  } else {
    // Ensure it's removed if this is a manual escape
    targetUrl = stripIabEscapeParam(targetUrl)
  }

  console.log('[IAB Escape] Attempting to escape from:', browserInfo.name)
  performRedirect(targetUrl, browserInfo)
}
